/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { restartableTask } from 'ember-concurrency';
import lodashSum from 'lodash.sum';
import MetricsService from 'cio/services/metrics';
import EnvironmentService from 'cio/services/environment';
import { getGroupingRanges } from 'cio/utils/metrics/common';
import TransactionalMessageModel from 'cio/models/transactional-message';
import { Metric } from 'cio/utils/metrics/models/metric';
import { Entities, Periods, MetricDataPoint, buildMetricGroupingOptions } from 'cio/utils/metrics/common';
import { findDenominator, MeasureType } from 'cio/utils/metrics/measure';
import { transactionalMetrics } from 'cio/utils/metrics/transactional';
import {
  LAST_30_DAYS_RANGE_PRESET_UTC,
  LAST_12_WEEKS_FROM_NOW_RANGE_PRESET_UTC,
  LAST_12_MONTHS_RANGE_PRESET,
} from 'cio/utils/workflow-metrics';
import moment from 'moment-timezone';
import { Grouping } from 'cio/utils/metrics/resolution';
import { TimeRange } from 'cio/utils/time';

interface TransactionalDeliveryMetricsArgs {
  message: TransactionalMessageModel;
  readOnly: boolean;
  grouping: Grouping;
  timeRange: TimeRange;
  onGroupingChange: (grouping: Grouping) => unknown;
  onRangeSelect: ({ date }: { date: TimeRange }) => unknown;
}

export default class TransactionalDeliveryMetrics extends Component<TransactionalDeliveryMetricsArgs> {
  @service metrics!: MetricsService;
  @service environment!: EnvironmentService;

  @tracked metric!: Metric;

  get groupings() {
    return buildMetricGroupingOptions(this.args.timeRange);
  }

  get groupedMetrics() {
    let groups: Record<string, Record<'values' | 'ratios', (number | null)[]>> = {};

    if (!this.metric) {
      return groups;
    }

    transactionalMetrics(this.args.message.type).forEach((metric) => {
      let values = (this.metric[metric]?.totals as MetricDataPoint[])?.map((m) => m.y) ?? [];
      let ratios =
        (this.metric[metric]?.rates as MetricDataPoint[])?.map((m) => (m.y == null ? null : m.y * 100)) ?? [];

      if (this.args.grouping === Grouping.Daily) {
        groups[metric] = { values, ratios };
      } else {
        // For other types of grouping, we'll have to calculate the values
        // ourselves. The numeratorValues are simple: we just splice every X
        // days and sum them.
        //
        // The denominator values depend on the metric type (for example, the
        // clicked ratio is clicked / sent). We extract them and then group
        // them using the same ranges. Finally, we group the two together.
        let ranges = getGroupingRanges(this.args.timeRange, Periods.CUSTOM, this.args.grouping);
        let numeratorRanges = ranges.map((len: number) => lodashSum(values.splice(0, len)));

        let denominator = findDenominator(metric, MeasureType.DeliveryMetrics);
        if (!denominator) {
          groups[metric] = { values: numeratorRanges, ratios: [] };
        } else {
          let denominatorValues = this.metric[denominator]!.totals.map((m) => m.y) ?? [];
          let denominatorRanges = ranges.map((len: number) => lodashSum(denominatorValues.splice(0, len)));

          groups[metric] = {
            values: numeratorRanges,
            ratios: numeratorRanges.map((v, idx) => (v / denominatorRanges[idx] || 0) * 100),
          };
        }
      }
    });

    return groups;
  }

  @action
  onRangeSelect({ date }: { date: TimeRange }) {
    if (!date?.end || !date?.start) {
      return;
    }

    this.args.onRangeSelect({ date });
  }

  @action
  onGroupingChange(grouping: Grouping) {
    this.args.onGroupingChange(grouping);
  }

  @action
  setDefaultGrouping() {
    let isAvailable = this.groupings.some((g) => g.value === this.args.grouping);
    if (!isAvailable) {
      this.args.onGroupingChange(Grouping.Daily);
    }
  }

  metricTask = restartableTask(async () => {
    this.metric = await this.metrics.fetchMetric(
      Entities.TRANSACTIONAL,
      this.args.message.id,
      Periods.CUSTOM,
      this.args.timeRange,
      'email',
      {
        environmentId: this.environment.current.id,
      },
    );
  });

  get dateRangePresets() {
    let allTimePreset = {
      label: 'All time',
      value: Periods.ALL_TIME,
      getRange: () => ({
        start: moment.unix(this.environment.current.createdAt).utc().startOf('day'),
        end: moment().utc().endOf('day'),
      }),
    };

    return [
      allTimePreset,
      LAST_30_DAYS_RANGE_PRESET_UTC,
      LAST_12_WEEKS_FROM_NOW_RANGE_PRESET_UTC,
      LAST_12_MONTHS_RANGE_PRESET,
    ];
  }
}
